/**
 * TimeRuler — horizontal time ruler pinned along the bottom of the Structured
 * A/V timeline. Renders a tick at every second at
 * `left: TIMELINE_INSET_PX + scale.x(i)`, labelled (0s, 1s, 2s, ...) as
 * densely as the current zoom allows, plus a minor tick at the half-second
 * mark. The same `TimeScale` positions the video / dialogue / SFX blocks, so
 * every block lines up with the ruler on a SHARED, sideways-scrolling
 * timeline.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'

/** Horizontal scales (pixels per second) the timeline zoom buttons step
 * through. Longer clips scroll sideways rather than shrinking. */
export const TIMELINE_ZOOM_LEVELS: readonly number[] = [16, 24, 32, 48, 64, 96, 128]
export const DEFAULT_TIMELINE_ZOOM_INDEX = 4

/** Left gutter before the 0s mark so a block starting at 0s does not touch
 * the panel edge. Shared by the ruler and the block slots. */
export const TIMELINE_INSET_PX = 8

/** Width of the column pinned to the timeline's right edge (vertical
 * add-block buttons above, zoom buttons beside the ruler). */
export const TIMELINE_SIDE_COLUMN_PX = 56

/** Minimum spacing between two tick labels, so "12s" and "13s" never touch. */
const MIN_LABEL_SPACING_PX = 28
const LABEL_STEPS = [1, 2, 5, 10, 30]

/** Maps seconds to horizontal pixels (before `TIMELINE_INSET_PX`). */
export interface TimeScale {
  x: (sec: number) => number
  /** Pixel position of `maxSec`. */
  width: number
  /** The zoom level's scale; stretched spans are never narrower than this. */
  basePixelsPerSecond: number
}

/** A block the scale must leave room for. */
export interface TimeScaleSpan {
  startSec: number
  endSec: number
  minWidthPx: number
}

/**
 * Build a piecewise-linear time scale. Every span starts at
 * `basePixelsPerSecond`; wherever a block would be narrower than its minimum
 * readable width, the time between its start and end is stretched until it
 * fits. Blocks are then drawn exactly from `x(startSec)` to `x(endSec)`, so a
 * short block never has to overflow into (and be covered by) the next one, and
 * the ruler — drawn with the same scale — stays aligned with every block edge.
 * Stretching only ever widens spans, so fixing one block never shrinks another.
 */
export function buildTimeScale(
  spans: readonly TimeScaleSpan[],
  maxSec: number,
  basePixelsPerSecond: number,
): TimeScale {
  const bounds = Array.from(
    new Set([0, maxSec, ...spans.flatMap((span) => [span.startSec, span.endSec])]),
  )
    .filter((sec) => sec >= 0 && sec <= maxSec)
    .sort((a, b) => a - b)
  const widths = bounds.slice(1).map((sec, i) => (sec - bounds[i]) * basePixelsPerSecond)

  for (const span of spans) {
    const i0 = bounds.indexOf(span.startSec)
    const i1 = bounds.indexOf(span.endSec)
    // Zero-length or out-of-range spans cannot be widened by stretching time.
    if (i0 < 0 || i1 <= i0) continue
    let current = 0
    for (let k = i0; k < i1; k++) current += widths[k]
    const deficit = span.minWidthPx - current
    if (deficit <= 0) continue
    // Spread the extra width across the span in proportion to duration.
    const duration = span.endSec - span.startSec
    for (let k = i0; k < i1; k++) {
      widths[k] += (deficit * (bounds[k + 1] - bounds[k])) / duration
    }
  }

  const offsets = [0]
  for (const w of widths) offsets.push(offsets[offsets.length - 1] + w)
  const width = offsets[offsets.length - 1]

  const x = (sec: number): number => {
    if (sec <= 0) return sec * basePixelsPerSecond
    if (sec >= maxSec) return width + (sec - maxSec) * basePixelsPerSecond
    let k = 0
    while (k < widths.length - 1 && bounds[k + 1] < sec) k++
    const segment = bounds[k + 1] - bounds[k]
    return offsets[k] + (segment > 0 ? ((sec - bounds[k]) / segment) * widths[k] : 0)
  }

  return { x, width, basePixelsPerSecond }
}

interface TimeRulerProps {
  maxSec: number
  scale: TimeScale
}

export const TimeRuler = memo(function TimeRuler({ maxSec, scale }: TimeRulerProps) {
  const { t } = useTranslation('canvas')
  const ticks: number[] = []
  // Render an integer tick at every second from 0 to ceil(maxSec). At least 1
  // tick (0s) is always shown so the ruler is visible for empty timelines.
  const last = Math.max(1, Math.ceil(maxSec))
  for (let i = 0; i <= last; i++) ticks.push(i)
  const labelStep =
    LABEL_STEPS.find((step) => step * scale.basePixelsPerSecond >= MIN_LABEL_SPACING_PX) ??
    LABEL_STEPS[LABEL_STEPS.length - 1]
  return (
    <div
      className="relative h-7 w-full shrink-0"
      data-testid="av-time-ruler"
      aria-label={t('timeRuler.timeRuler')}
    >
      {ticks.map((sec) => (
        <div
          key={`tick-${sec}`}
          className={`absolute top-0 bottom-0 flex flex-col ${sec === 0 ? 'items-start' : 'items-center -translate-x-1/2'}`}
          style={{ left: TIMELINE_INSET_PX + scale.x(sec) }}
          data-testid={`av-time-tick-${sec}`}
        >
          <span className="block w-px h-2 bg-text-muted/40" aria-hidden />
          {sec % labelStep === 0 && (
            <span className="text-xxs font-mono leading-3 mt-1 text-text-muted">
              {sec}s
            </span>
          )}
        </div>
      ))}
      {/* Minor half-second ticks (visual only, no label) */}
      {ticks.slice(0, -1).map((sec) => (
        <span
          key={`minor-${sec}`}
          className="absolute top-0 block w-px h-1 bg-text-muted/20"
          style={{ left: TIMELINE_INSET_PX + scale.x(sec + 0.5) }}
          aria-hidden
        />
      ))}
    </div>
  )
})

TimeRuler.displayName = 'TimeRuler'
